import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

/**
 * One-tap phone-call button. Renders as a `tel:` anchor styled like the
 * WhatsApp button so they sit side-by-side in the UI.
 *
 * Props:
 *   phone      — raw phone string (any format)
 *   size       — "sm" | "default" | "icon"  (default: "sm")
 *   variant    — Button variant            (default: "outline")
 *   label      — Button label when not icon-only (default: "Call")
 *   testId     — data-testid override
 *   onCall     — optional callback when user taps
 */
export default function CallButton({
  phone,
  size = "sm",
  variant = "outline",
  label = "Call",
  testId,
  onCall,
}) {
  if (!phone) return null;

  // Strip everything but digits and a leading "+" so the OS dialer
  // accepts it on every platform.
  const trimmed = String(phone).trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;
  const tel = (hasPlus ? "+" : "") + digits;

  // "icon" / "icon-sm" both render as a square icon-only chip. icon-sm is
  // 36×36 (matches the row icons), plain "icon" is the shadcn default 40×40.
  const isIconOnly = size === "icon" || size === "icon-sm";
  const isCompactIcon = size === "icon-sm";
  // shadcn's Button only knows default|sm|lg|icon — coerce icon-sm → icon.
  const buttonSize = isCompactIcon ? "icon" : size;

  return (
    <Button
      asChild
      type="button"
      variant={variant}
      size={buttonSize}
      className={`rounded-2xl tap text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 ${isCompactIcon ? "h-9 w-9" : ""}`}
      data-testid={testId || "call-button"}
    >
      <a
        href={`tel:${tel}`}
        onClick={(e) => {
          e.stopPropagation();
          if (onCall) onCall();
        }}
        aria-label={`Call ${tel}`}
      >
        <Phone className={isIconOnly ? "h-4 w-4" : "h-3.5 w-3.5 mr-1"} />
        {!isIconOnly && label}
      </a>
    </Button>
  );
}
